import { Flex, Text } from '@chakra-ui/react';
import { useSubmissionData } from '../../../contexts/SubmissionDataProvider';
import { fetchSubmissionIntegrityReports } from '../../../api/queries';
import { useQuery } from '@tanstack/react-query';
import { CheckResults } from './CheckResults';
import { Accordion, Badge } from '@morressier/alembic';
import { IGetRevisionHistory } from '@publishing-engine/submission-service/src/endpoints/api.response-types';
import dateFormat from 'dateformat';
import Loading from 'src/pages/Status/Loading';
import StatusDot from './StatusDot';
import { formatDate } from 'src/utils/date';

const getAccordionTitle = (
  revision: IGetRevisionHistory,
  round: number,
  ManuscriptId?: string
) => {
  const date = `${dateFormat(
    revision?.dateEntered!,
    'mmmm d, yyyy'
  )} to ${dateFormat(revision?.dateCompleted!, 'mmmm d, yyyy')}`;

  const idPrefix = ManuscriptId ? `${ManuscriptId} | ` : '';

  return round === 1
    ? `${idPrefix}  Original Submission (${date})`
    : `${idPrefix}  Revision - ${round - 1} (${date})`;
};
const IntegrityTab: React.FC<{}> = () => {
  const { workflowResponse, revisionHistory } = useSubmissionData();
  const submissionId = workflowResponse.submissionId;
  const {
    data: overViewIntegrityData,
    isLoading,
    isFetching,
    refetch
  } = useQuery({
    queryKey: ['submission-integrity', { submissionId }],
    queryFn: () => fetchSubmissionIntegrityReports(submissionId),
    retry: 0
  });
  const IntegrityData = overViewIntegrityData?.result;
  const [FirstIntegrity, ...Integrity] = IntegrityData ?? [];
  if (isLoading || !Integrity) {
    return <Loading />;
  }

  const Heading = () => {
    return (
      <Flex flexDirection="column" gap={2}>
        <Flex justifyContent="space-between">
          <Text textStyle="headingSection">
            Integrity checks{'  '}
            <StatusDot status={overViewIntegrityData?.overallStatus} />
          </Text>
        </Flex>
      </Flex>
    );
  };

  if (!IntegrityData?.length) {
    return <Heading />;
  }

  return (
    <Flex flexDirection="column" gap={4}>
      <Heading />
      {FirstIntegrity && (
        <CheckResults
          Integrity={FirstIntegrity}
          IsLoading={isFetching}
          latestIntegrity={true}
          refetch={refetch}
          revisionHistory={
            revisionHistory?.current?.rounds[IntegrityData?.length - 1]
          }
          submissionContextId={workflowResponse?.submissionContextId}
          currentState={workflowResponse?.workflowData?.currentState}
        />
      )}
      {Integrity.length >= 1 && (
        <Text textStyle="headingSection">Report History</Text>
      )}

      <Accordion
        data-testId="integrity-overview"
        chakraAccordionProps={{
          allowMultiple: true,
          defaultIndex: [],
          style: {
            display: 'flex',
            flexDirection: 'column'
          }
        }}
        accordionItemProps={{
          background: 'neutralPrimaryContentInverse',
          marginBottom: 1
        }}
        items={Integrity?.map((IntegrityRound, index) => {
          return {
            title:
              revisionHistory &&
              getAccordionTitle(
                revisionHistory?.current?.rounds[index] as IGetRevisionHistory,
                Number(IntegrityRound?.roundId) + 1
              ),
            subtitle: `Manuscript updated on ${dateFormat(
              IntegrityRound?.checkResults[0]?.created_at,
              'mmmm d, yyyy'
            )}`,
            content: (
              <CheckResults
                key={IntegrityRound.roundId}
                Integrity={IntegrityRound}
                IsLoading={isFetching}
                refetch={refetch}
                submissionContextId={workflowResponse.submissionContextId}
                currentState={workflowResponse.workflowData.currentState}
                revisionHistory={revisionHistory?.current?.rounds[index]}
              />
            )
          };
        })}
      />
      {overViewIntegrityData?.pastJournalHistory &&
        overViewIntegrityData?.pastJournalHistory.length >= 1 && (
          <Text textStyle={'headingSection'}> Transfer History</Text>
        )}

      {overViewIntegrityData?.pastJournalHistory.map(
        (JournalHistory, JournalHistoryIndex) => {
          const { fromJournalName, date, toJournalName, fromManuscriptId } =
            JournalHistory.transferedDetail;
          return (
            <>
              <Flex flexDirection="column" gap={3} mb={3}>
                <Flex justifyContent="space-between">
                  <Text textStyle="textBody" fontWeight="semibold">
                    {fromJournalName} Report
                  </Text>
                  <Badge variant={'neutral'} size="context">
                    {'Transferred'}
                  </Badge>
                </Flex>
                {
                  <Text textStyle="textBody">
                    Transferred to {toJournalName} on{' '}
                    {formatDate(new Date(date ?? ''))}
                  </Text>
                }
              </Flex>
              <Accordion
                data-testId="integrity-overview"
                chakraAccordionProps={{
                  allowMultiple: true,
                  defaultIndex: [],
                  style: {
                    display: 'flex',
                    flexDirection: 'column'
                  }
                }}
                accordionItemProps={{
                  background: 'neutralPrimaryContentInverse',
                  marginBottom: 1
                }}
                items={JournalHistory.result?.map((IntegrityRound, index) => {
                  return {
                    title:
                      revisionHistory &&
                      getAccordionTitle(
                        revisionHistory?.previous[JournalHistoryIndex]?.rounds[
                          index
                        ] as IGetRevisionHistory,
                        Number(IntegrityRound?.roundId) + 1,
                        fromManuscriptId
                      ),
                    subtitle: `Manuscript updated on ${dateFormat(
                      IntegrityRound?.checkResults[0]?.created_at,
                      'mmmm d, yyyy'
                    )}`,
                    content: (
                      <CheckResults
                        key={IntegrityRound.roundId}
                        Integrity={IntegrityRound}
                        IsLoading={isFetching}
                        refetch={refetch}
                        submissionContextId={
                          workflowResponse.submissionContextId
                        }
                        currentState={
                          workflowResponse.workflowData.currentState
                        }
                        revisionHistory={
                          revisionHistory?.current?.rounds[index]
                        }
                      />
                    )
                  };
                })}
              />
            </>
          );
        }
      )}
    </Flex>
  );
};

export default IntegrityTab;
